import { useLocation } from 'react-router';
import { useCrmOrgs } from './useCrmOrgs';
import { useRunOnce } from '../../common/useRunOnce';
import {
  CrmOrgConnectingError,
  getCrmOrgTransientData,
  setCrmOrgTransientResult,
} from './connectCrmSessionHelper';
import LoadingSweep from '../../common/LoadingSweep';
import { SweepError } from '@server/sweep-errors';
import axios from 'axios';
import { telemetry } from '../../../telemetry';
import { useEnsureLdClient } from '../../../feature-flags/useEnsureLdClient';
import { useInitializeLd } from '../../../feature-flags/useInitializeLD';

const wait = (ms: number = 2000) => new Promise((resolve) => setTimeout(resolve, ms));

const SalesforceOAuth2Redirect = () => {
  const { search } = useLocation();
  const { setSalesforceOauthCode } = useCrmOrgs();
  const { initializeLD } = useInitializeLd();
  const ldClient = useEnsureLdClient();

  /**
   * NOTE: need to make sure all the known scenarios are calling "setCrmOrgTransientResult"
   * So if there's no data, it means the user MANUALLY closed the browser's tab
   **/

  useRunOnce(async () => {
    await ldClient.waitUntilReady();
    const featureFlags = await initializeLD();
    const isProcessMiningEnabled = featureFlags?.['processMiningV2'];

    const urlSearchParams = new URLSearchParams(search);
    const code = urlSearchParams.get('code');
    const error = urlSearchParams.get('error');
    const errorDescription = urlSearchParams.get('error_description');
    const connectionData = getCrmOrgTransientData();
    if (!connectionData) {
      return <LoadingSweep />;
    }

    if (error === 'access_denied' && errorDescription === 'end-user denied authorization') {
      setCrmOrgTransientResult(
        {
          success: false,
          error: CrmOrgConnectingError.USER_DENIED_AUTHORIZATION,
        },
        connectionData.crmOrgId,
      );
      await wait(); // wait for the error to be set in local storage
      window.close();
      return;
    }

    if (connectionData && code) {
      try {
        await setSalesforceOauthCode({
          authCode: code,
          crmOrgId: connectionData.crmOrgId,
          isSandbox: connectionData.isSandbox,
          isOnboardingFastFetch: connectionData.isOnboardingFastFetch,
          isProcessMiningEnabled,
        });
        setCrmOrgTransientResult(
          {
            success: true,
          },
          connectionData.crmOrgId,
        );
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          error.response?.status === 401 &&
          error.response.data.sweepError === SweepError.CrmOrgInvalidGrant
        ) {
          setCrmOrgTransientResult(
            {
              success: false,
              error: CrmOrgConnectingError.NOT_ENOUGH_PERMISSIONS,
            },
            connectionData.crmOrgId,
          );
        } else if (
          axios.isAxiosError(error) &&
          error.response?.status === 409 &&
          error.response.data.sweepError === SweepError.CrmOrgAlreadyConnected
        ) {
          setCrmOrgTransientResult(
            {
              success: false,
              error: CrmOrgConnectingError.ORG_ALREADY_CONNECTED_TO_SWEEP,
              retryData: {
                isSameAccount: error.response.data.isSameAccount ?? false,
              },
            },
            connectionData.crmOrgId,
          );
        } else {
          setCrmOrgTransientResult(
            {
              success: false,
              error: CrmOrgConnectingError.ERROR_CONNECTING,
            },
            connectionData.crmOrgId,
          );
          telemetry.captureError(error);
        }
      }
    }
    await wait(); // wait for the error to be set in local storage
    window.close();
  });

  return <LoadingSweep />;
};

export { SalesforceOAuth2Redirect };
